const CPC_SIZE_ORDER = ['YXXS', 'YXS', 'YS', 'YM', 'YL', 'YXL', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

function cpcSortVariants(variants) {
  return [...variants].sort((a, b) => {
    const ai = CPC_SIZE_ORDER.indexOf((a.title || '').toUpperCase());
    const bi = CPC_SIZE_ORDER.indexOf((b.title || '').toUpperCase());
    const aN = parseFloat(a.title);
    const bN = parseFloat(b.title);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    if (!isNaN(aN) && !isNaN(bN)) return aN - bN;
    return (a.title || '').localeCompare(b.title || '');
  });
}

function cpcFormatMoney(cents) {
  if (cents == null) return '';
  const dollars = (typeof cents === 'number' ? cents : parseFloat(cents)) / 100;
  // Always 2 decimals to match the Liquid `money` filter (e.g. "$25.00 USD").
  return `$${dollars.toFixed(2)} USD`;
}

// Builds the inner markup for the card price container ([ref="priceContainer"]) for a single
// variant, mirroring the Liquid render: a regular line plus a struck-through sale line when the
// variant's compare-at price is higher. The currency is wrapped in .cpc__currency to match.
function cpcPriceHTML(priceCents, compareCents) {
  const price = parseInt(priceCents, 10);
  const compare = compareCents ? parseInt(compareCents, 10) : 0;
  const onSale = Number.isFinite(compare) && compare > price;
  const cur = '<span class="cpc__currency">USD</span>';
  const money = (c) => `$${(c / 100).toFixed(2)}`;
  let html = `<div class="price__regular"><span class="price-item price-item--regular">${money(price)} ${cur}</span></div>`;
  if (onSale) {
    html += `<div class="price__sale"><s class="price-item price-item--regular">${money(compare)}</s><span class="price-item price-item--sale">${money(price)} ${cur}</span></div>`;
  }
  return { html, onSale };
}

class CustomProductCard extends HTMLElement {
  connectedCallback() {
    this._selectedVariantId = null;
    this._qvSelectedVariantId = null;
    this._qvQty = 1;
    // The quick-view modal is server-rendered for the original product. When a
    // swatch swaps the card to a different colour (a separate product), this
    // holds that product's data and flags the modal for a rebuild on next open.
    this._activeProduct = null;
    this._qvStale = false;

    this._boundOnClick = this._onClick.bind(this);
    this.addEventListener('click', this._boundOnClick);

    // Cache modal roots. They get portaled to <body> on open so they escape
    // any ancestor section that clips them (transform/contain/overflow create
    // a containing block for the fixed overlay, cutting it off mid-page).
    this._qvModal = this.querySelector('[data-cpc-qv-modal]');
    this._videoModal = this.querySelector('[data-cpc-video-modal]');
    // Cache the default (no-variant-selected) price markup so we can restore the price range
    // after a variant is deselected. We update the contents of [ref="priceContainer"] rather
    // than replacing it, to keep the node product-price.js relies on intact.
    this._priceContainer = this.querySelector('.cpc__price [ref="priceContainer"]');
    if (this._priceContainer) {
      this._defaultPriceHTML = this._priceContainer.innerHTML;
      this._defaultPriceClass = this._priceContainer.className;
    }

    this._escHandler = (e) => {
      if (e.key === 'Escape') this._closeAll();
    };
    document.addEventListener('keydown', this._escHandler);

    this._initHoverImage();
    this._bindMobileAtc();
  }

  // Move a modal to <body> (once) and bind the same click handler so its
  // interactions keep working from its new location in the DOM.
  _portal(modal) {
    if (!modal) return;
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }
    if (!modal._cpcBound) {
      modal.addEventListener('click', this._boundOnClick);
      modal._cpcBound = true;
    }
  }

  _bindMobileAtc() {
    const btn = this.querySelector('.cpc__mobile-atc:not([disabled])');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._openQv();
    });
  }

  _initHoverImage() {
    // Inline onmouseenter/onmouseleave on .cpc__img-link handles the swap.
    // This method keeps the hover src in sync after swatch updates.
  }

  // Called by product-color-swatches after updating images
  updateHoverSrc(newSrc) {
    const link = this.querySelector('.cpc__img-link');
    if (!link || !newSrc) return;
    link.setAttribute(
      'onmouseenter',
      `var p=this.querySelector('img');if(p){p._s=p.src;p.src='${newSrc}'}`
    );
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._escHandler);
    // Re-home any portaled modals so they're garbage-collected with this card
    // (and available again if the same element is reconnected).
    [this._qvModal, this._videoModal].forEach((m) => {
      if (m && m.parentNode === document.body) this.appendChild(m);
    });
    document.body.style.overflow = '';
  }

  // ── Event delegation ──────────────────────────────────────────────────────
  _onClick(e) {
    const t = e.target;

    // Variant button (card hover)
    const vBtn = t.closest('.cpc__variant-btn:not([disabled])');
    if (vBtn) {
      e.preventDefault();
      this._selectVariant(vBtn);
      return;
    }

    // Add to cart (card)
    const atcBtn = t.closest('[data-cpc-atc]');
    if (atcBtn && !atcBtn.disabled) {
      e.preventDefault();
      this._addToCart();
      return;
    }

    // Play video
    const playBtn = t.closest('[data-cpc-play]');
    if (playBtn) {
      e.preventDefault();
      e.stopPropagation();
      this._openVideo(playBtn.dataset.videoId);
      return;
    }

    // Quick view button (card)
    if (t.closest('[data-cpc-open-qv]')) {
      e.preventDefault();
      e.stopPropagation();
      this._openQv();
      return;
    }

    // Video → Quick View
    if (t.closest('[data-cpc-video-to-qv]')) {
      e.preventDefault();
      e.stopPropagation();
      this._closeVideo();
      this._openQv();
      return;
    }

    // Close video
    if (t.closest('[data-cpc-close-video]')) {
      e.preventDefault();
      this._closeVideo();
      return;
    }

    // Close QV
    if (t.closest('[data-cpc-close-qv]')) {
      e.preventDefault();
      this._closeQv();
      return;
    }

    // Modal background click to close
    if (t.hasAttribute('data-cpc-modal-bg') || t.classList.contains('cpc__modal-bg')) {
      e.preventDefault();
      this._closeAll();
      return;
    }

    // QV variant
    const qvVBtn = t.closest('.cpc__qv-variant-btn:not([disabled])');
    if (qvVBtn) {
      e.preventDefault();
      this._qvSelectVariant(qvVBtn);
      return;
    }

    // QV quantity
    const qtyBtn = t.closest('[data-cpc-qty]');
    if (qtyBtn) {
      e.preventDefault();
      this._qvChangeQty(parseInt(qtyBtn.dataset.cpcQty));
      return;
    }

    // QV add to cart
    const qvAtc = t.closest('[data-cpc-qv-atc]');
    if (qvAtc && !qvAtc.disabled) {
      e.preventDefault();
      this._qvAddToCart();
      return;
    }
  }

  // ── Variant selection (card) ──────────────────────────────────────────────
  _selectVariant(btn) {
    const id = btn.dataset.variantId;
    const alreadySelected = this._selectedVariantId === id;

    this.querySelectorAll('.cpc__variant-btn').forEach((b) =>
      b.classList.remove('cpc__variant-btn--selected')
    );

    if (alreadySelected) {
      this._selectedVariantId = null;
      this._restoreCardPrice();
    } else {
      this._selectedVariantId = id;
      btn.classList.add('cpc__variant-btn--selected');
      this._setCardPrice(btn.dataset.variantPrice, btn.dataset.variantCompareAtPrice);
    }
    this._refreshAtcBtn();
  }

  // ── Card price display ──────────────────────────────────────────────────────
  // Swap the displayed price to the selected variant's price.
  _setCardPrice(priceCents, compareCents) {
    if (!this._priceContainer || priceCents == null || priceCents === '') return;
    const { html, onSale } = cpcPriceHTML(priceCents, compareCents);
    this._priceContainer.className = onSale ? 'price price--on-sale' : 'price';
    this._priceContainer.innerHTML = html;
  }

  // Restore the default price (the range, or single price) shown when nothing is selected.
  _restoreCardPrice() {
    if (!this._priceContainer || this._defaultPriceHTML == null) return;
    this._priceContainer.className = this._defaultPriceClass;
    this._priceContainer.innerHTML = this._defaultPriceHTML;
  }

  _refreshAtcBtn() {
    const btn = this.querySelector('[data-cpc-atc]');
    if (!btn) return;
    const hasVariants = this.dataset.hasVariants === 'true';
    const available = this.dataset.productAvailable !== 'false';

    if (!available) {
      btn.disabled = true;
      btn.textContent = 'Sold Out';
      return;
    }
    if (!hasVariants) {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
      return;
    }

    if (this._selectedVariantId) {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
    } else {
      btn.disabled = true;
      btn.textContent = 'Select a Size';
    }
  }

  // ── Add to cart ───────────────────────────────────────────────────────────
  _resolveVariantId() {
    const hasVariants = this.dataset.hasVariants === 'true';
    if (!hasVariants) {
      return this.querySelector('[data-variant-id]')?.dataset.variantId;
    }
    return this._selectedVariantId;
  }

  _addToCart(variantId = null, qty = 1) {
    const vid = variantId || this._resolveVariantId();
    if (!vid) return;

    const id = String(vid).includes('/') ? String(vid).split('/').pop() : String(vid);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: qty }] }),
    })
      .then((r) => r.json())
      .then(() => {
        // Notify theme cart drawer / bubble
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch((err) => console.error('[cpc] add-to-cart error:', err));
  }

  // ── Video modal ───────────────────────────────────────────────────────────
  _openVideo(videoId) {
    const modal = this._videoModal;
    if (!modal) return;
    this._portal(modal);

    const iframe = modal.querySelector('[data-cpc-video-iframe]');
    if (iframe && videoId) {
      iframe.src = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1&enablejsapi=1&controls=0&loop=1`;
    }

    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  _closeVideo() {
    const modal = this._videoModal;
    if (!modal) return;
    const iframe = modal.querySelector('[data-cpc-video-iframe]');
    if (iframe) iframe.src = '';
    modal.hidden = true;
    this._restoreScroll();
  }

  // ── Quick view modal ──────────────────────────────────────────────────────
  // Called by product-color-swatches.js when a swatch swaps the card to a
  // different colour (a separate product). The visible card is updated there;
  // here we just record the active product and flag the quick-view as stale so
  // it rebuilds from this product the next time it opens.
  _setActiveProduct(productData) {
    if (!productData) return;
    this._activeProduct = productData;
    this._qvStale = true;
  }

  _openQv() {
    const modal = this._qvModal;
    if (!modal) return;
    this._portal(modal);

    // If a swatch switched the card to a different product, the server-rendered
    // quick-view still shows the original — rebuild it so the modal matches the
    // colour the shopper is looking at. Done before measuring the fly-from rect.
    if (this._qvStale && this._activeProduct) {
      this._rebuildQv(this._activeProduct);
      this._qvStale = false;
    } else {
      // First open of the server-rendered modal: expand the JSON manifest of
      // the remaining gallery images (only the first was rendered up front).
      this._ensureQvGallery(modal);
    }

    // Cancel any in-flight close so a quick reopen lands in a clean state.
    clearTimeout(this._qvCloseTimer);
    modal.classList.remove('cpc__qv-modal--closing');

    // Capture the card image's on-screen rect *before* showing the modal so we
    // can fly it into the gallery (shared-element transition). Measured here
    // because portaling/showing the modal doesn't move the card.
    const flyFrom = this._captureFlyFrom();

    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    this._flyCardImageIntoQv(modal, flyFrom);

    // The content's enter animation is force-retriggered above (the --flying
    // class + the fly's reflow restart it every open). The backdrop's fade has
    // no such retrigger — it relies only on the display:none→block animation
    // restart, which the fly's synchronous reflow swallows (and which never
    // fires on a rapid reopen, where `hidden` was never toggled). That's the
    // hard flash. Restart the fade explicitly so the backdrop always fades in.
    const bg = modal.querySelector('.cpc__modal-bg');
    if (bg) {
      bg.style.animation = 'none';
      void bg.offsetWidth; // force reflow so re-adding the animation restarts it
      bg.style.animation = '';
    }
  }

  // Expand the server-rendered gallery's JSON manifest into <img> elements the
  // first time the modal opens. The Liquid renders only the first image up front
  // (the FLIP target) and emits the rest as a manifest to keep a page of cards
  // light; this appends them once, keyed off a build flag so it never doubles up.
  _ensureQvGallery(modal) {
    const gallery = modal.querySelector('.cpc__qv-gallery');
    if (!gallery || gallery.dataset.cpcQvBuilt) return;
    gallery.dataset.cpcQvBuilt = '1';

    const manifest = modal.querySelector('[data-cpc-qv-media]');
    if (!manifest) return;

    let media = [];
    try {
      media = JSON.parse(manifest.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(media) || !media.length) return;

    const esc = (s) => String(s == null ? '' : s).replace(/"/g, '&quot;');
    // The first image (data-qv-img="0") is already in the DOM, so the manifest
    // (images 1+) continues the index from 1.
    const html = media
      .map((m, i) => {
        const idx = i + 1;
        return `<img src="${m.src}" alt="${esc(m.alt)}" class="cpc__qv-img" data-qv-img="${idx}" width="800" height="800" loading="lazy">`;
      })
      .join('');
    gallery.insertAdjacentHTML('beforeend', html);
  }

  // Rebuild the quick-view modal's contents (gallery, title, price, sizes, ATC,
  // full-product link) from a product's JSON. Used when a swatch has swapped the
  // card to a different colour so the modal reflects the selected product.
  _rebuildQv(p) {
    const modal = this._qvModal;
    if (!modal || !p) return;

    const money = (c) => `$${(parseInt(c, 10) / 100).toFixed(2)}`;
    const cur = '<span class="cpc__currency">USD</span>';
    const esc = (s) => String(s == null ? '' : s).replace(/"/g, '&quot;');

    // ── Gallery ──
    const gallery = modal.querySelector('.cpc__qv-gallery');
    if (gallery) {
      const media =
        p.media && p.media.length
          ? p.media
          : (p.images || []).map((src) => ({ src, alt: p.title }));
      gallery.innerHTML = media
        .map((m, i) => {
          const src = m.src || m;
          return `<img src="${src}" alt="${esc(m.alt || p.title)}" class="cpc__qv-img" data-qv-img="${i}" width="800" height="800" loading="${i === 0 ? 'eager' : 'lazy'}">`;
        })
        .join('');
      // This rebuild already contains every image, so the manifest expansion in
      // _ensureQvGallery must not run (or it would duplicate them).
      gallery.dataset.cpcQvBuilt = '1';
    }

    // ── Title (name + " - Colour" suffix) ──
    const titleEl = modal.querySelector('.cpc__qv-title');
    if (titleEl) {
      const [name, color] = (p.title || '').split(' - ');
      titleEl.textContent = name || p.title || '';
      if (color) {
        const span = document.createElement('span');
        span.className = 'cpc__title-color';
        span.textContent = color;
        titleEl.appendChild(span);
      }
    }

    // ── Price ──
    const priceEl = modal.querySelector('[data-cpc-qv-price]');
    if (priceEl) {
      if (p.price_varies) {
        priceEl.innerHTML = `${money(p.price_min)} – ${money(p.price_max)} ${cur}`;
      } else if (p.compare_at_price && p.compare_at_price > p.price) {
        priceEl.innerHTML = `<s>${money(p.compare_at_price)}</s> <span>${money(p.price)} ${cur}</span>`;
      } else {
        priceEl.innerHTML = `${money(p.price)} ${cur}`;
      }
    }

    // ── Size variants ──
    const variants = p.variants || [];
    const hasRealVariants =
      variants.length > 1 || (variants.length === 1 && variants[0].title !== 'Default Title');
    const variantsWrap = modal.querySelector('.cpc__qv-variants');
    const sizeWrap = modal.querySelector('.cpc__qv-size-wrap');
    if (variantsWrap && hasRealVariants) {
      variantsWrap.innerHTML = cpcSortVariants(variants)
        .map((v) => {
          const soldOut =
            v.inventory_quantity != null ? v.inventory_quantity <= 0 : v.available === false;
          const title = v.title === 'Default Title' ? 'One Size' : v.title || '';
          return `<button class="cpc__qv-variant-btn${soldOut ? ' cpc__qv-variant-btn--sold-out' : ''}" data-qv-variant-id="${v.id}" data-qv-variant-price="${v.price}" data-qv-variant-title="${esc(title)}" type="button"${soldOut ? ' disabled' : ''}>${title}</button>`;
        })
        .join('');
      if (sizeWrap) sizeWrap.style.display = '';
    } else if (sizeWrap) {
      sizeWrap.style.display = 'none';
      if (variantsWrap) variantsWrap.innerHTML = '';
    }

    // ── Full-product link ──
    const fullLink = modal.querySelector('.cpc__qv-full-link');
    if (fullLink) fullLink.href = p.url || `/products/${p.handle}`;

    // ── Reset selection + quantity for the freshly-swapped product ──
    this._qvSelectedVariantId = null;
    this._qvQty = 1;
    const qtyInput = modal.querySelector('.cpc__qv-qty-input');
    if (qtyInput) qtyInput.value = '1';
    const [minus, plus] = modal.querySelectorAll('[data-cpc-qty]');
    if (minus) minus.disabled = true;
    if (plus) plus.disabled = false;

    const atc = modal.querySelector('[data-cpc-qv-atc]');
    if (atc) {
      const soldOut = p.available === false;
      atc.disabled = soldOut || hasRealVariants;
      atc.textContent = soldOut ? 'Sold Out' : hasRealVariants ? 'Select a Size' : 'Add to Cart';
    }
  }

  // Grab the currently-visible card image and its viewport rect, or null if the
  // shared-element animation can't/shouldn't run (no image, reduced motion).
  _captureFlyFrom() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return null;
    }
    const img = this.querySelector('.cpc__img--primary');
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return { src: img.currentSrc || img.src, rect };
  }

  // FLIP the card image up into the first gallery slot of the quick-view modal.
  // The real gallery image is hidden until the flying clone lands on it, so the
  // card image appears to be lifted into the modal.
  _flyCardImageIntoQv(modal, from) {
    if (!from) return;
    const qvImg = modal.querySelector('.cpc__qv-img[data-qv-img="0"]');
    if (!qvImg) return;

    // Use the flying entrance (a subtle slide-up of the content) instead of the
    // base slide-up.
    modal.classList.add('cpc__qv-modal--flying');

    // Measure the gallery image's *final* resting spot with the content's
    // entrance animation neutralized — otherwise we'd read its mid-slide
    // position and the clone would land short. Restoring the inline value lets
    // the entrance animation play normally (no paint happens in between).
    const content = modal.querySelector('.cpc__qv-content');
    const prevAnim = content ? content.style.animation : null;
    if (content) content.style.animation = 'none';
    const to = qvImg.getBoundingClientRect();
    if (content) content.style.animation = prevAnim;

    if (!to.width || !to.height) {
      modal.classList.remove('cpc__qv-modal--flying');
      return;
    }

    const clone = document.createElement('img');
    clone.src = from.src;
    clone.className = 'cpc__qv-fly';
    clone.style.left = `${to.left}px`;
    clone.style.top = `${to.top}px`;
    clone.style.width = `${to.width}px`;
    clone.style.height = `${to.height}px`;

    // Inverse transform places the clone exactly over the card image to start.
    const dx = from.rect.left - to.left;
    const dy = from.rect.top - to.top;
    const sx = from.rect.width / to.width;
    const sy = from.rect.height / to.height;
    clone.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;

    document.body.appendChild(clone);
    qvImg.style.opacity = '0';

    // Note: the `cpc__qv-modal--flying` class stays on until the modal closes.
    // Removing it here would swap the content's `animation` back to the
    // slide-up rule and *replay* it, making the modal jump up after landing.
    const cleanup = () => {
      qvImg.style.opacity = '';
      clone.remove();
    };

    // Next frame: release the transform so it animates to its resting spot.
    requestAnimationFrame(() => {
      clone.classList.add('cpc__qv-fly--in');
      clone.style.transform = 'translate(0, 0) scale(1)';
    });

    clone.addEventListener('transitionend', cleanup, { once: true });
    // Safety net in case transitionend never fires (e.g. interrupted layout).
    setTimeout(cleanup, 700);
  }

  _closeQv() {
    const modal = this._qvModal;
    if (!modal || modal.hidden) return;

    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      this._finishCloseQv(modal);
      return;
    }

    // Reverse FLIP: fly the gallery image back onto the card while the drawer
    // slides away and the backdrop fades out.
    this._flyQvImageBackToCard(modal);
    modal.classList.add('cpc__qv-modal--closing');

    clearTimeout(this._qvCloseTimer);
    this._qvCloseTimer = setTimeout(() => this._finishCloseQv(modal), 360);
  }

  // Hide the modal and reset all transition state so the next open is clean.
  _finishCloseQv(modal) {
    modal.hidden = true;
    modal.classList.remove('cpc__qv-modal--flying', 'cpc__qv-modal--closing');
    const qvImg = modal.querySelector('.cpc__qv-img[data-qv-img="0"]');
    if (qvImg) qvImg.style.opacity = '';
    this._restoreScroll();
  }

  // Mirror of _flyCardImageIntoQv: clone the first gallery image and FLIP it
  // back down onto the card's primary image. Returns false if it can't run.
  _flyQvImageBackToCard(modal) {
    const qvImg = modal.querySelector('.cpc__qv-img[data-qv-img="0"]');
    const cardImg = this.querySelector('.cpc__img--primary');
    if (!qvImg || !cardImg) return false;

    const from = qvImg.getBoundingClientRect();
    const to = cardImg.getBoundingClientRect();
    if (!from.width || !from.height || !to.width || !to.height) return false;

    const clone = document.createElement('img');
    clone.src = qvImg.currentSrc || qvImg.src;
    clone.className = 'cpc__qv-fly cpc__qv-fly--in';
    clone.style.left = `${from.left}px`;
    clone.style.top = `${from.top}px`;
    clone.style.width = `${from.width}px`;
    clone.style.height = `${from.height}px`;
    clone.style.transform = 'translate(0, 0) scale(1)';

    document.body.appendChild(clone);
    qvImg.style.opacity = '0';

    const dx = to.left - from.left;
    const dy = to.top - from.top;
    const sx = to.width / from.width;
    const sy = to.height / from.height;

    requestAnimationFrame(() => {
      clone.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    });

    const remove = () => clone.remove();
    clone.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, 600);
    return true;
  }

  _closeAll() {
    this._closeVideo();
    this._closeQv();
  }

  _restoreScroll() {
    const videoOpen = this._videoModal ? !this._videoModal.hidden : false;
    const qvOpen = this._qvModal ? !this._qvModal.hidden : false;
    if (!videoOpen && !qvOpen) document.body.style.overflow = '';
  }

  // ── QV variant selection ──────────────────────────────────────────────────
  _qvSelectVariant(btn) {
    this._qvSelectedVariantId = btn.dataset.qvVariantId;

    const modal = this._qvModal;
    modal
      .querySelectorAll('.cpc__qv-variant-btn')
      .forEach((b) => b.classList.remove('cpc__qv-variant-btn--selected'));
    btn.classList.add('cpc__qv-variant-btn--selected');

    // Update price if variant-level pricing differs
    const variantPrice = btn.dataset.qvVariantPrice;
    if (variantPrice) {
      const priceEl = modal.querySelector('[data-cpc-qv-price]');
      if (priceEl) priceEl.textContent = cpcFormatMoney(parseInt(variantPrice));
    }

    const atcBtn = modal.querySelector('[data-cpc-qv-atc]');
    if (atcBtn) {
      atcBtn.disabled = false;
      atcBtn.textContent = 'Add to Cart';
    }

    // Also activate the same size on the card hover panel and sync the card price
    let matchedCardBtn = null;
    this.querySelectorAll('.cpc__variant-btn').forEach((b) => {
      const match = b.dataset.variantId === this._qvSelectedVariantId;
      b.classList.toggle('cpc__variant-btn--selected', match);
      if (match) matchedCardBtn = b;
    });
    if (matchedCardBtn) {
      this._setCardPrice(
        matchedCardBtn.dataset.variantPrice,
        matchedCardBtn.dataset.variantCompareAtPrice
      );
    }
    this._selectedVariantId = this._qvSelectedVariantId;
    this._refreshAtcBtn();
  }

  // ── QV quantity ───────────────────────────────────────────────────────────
  _qvChangeQty(delta) {
    this._qvQty = Math.max(1, Math.min(3, this._qvQty + delta));
    const modal = this._qvModal;
    const input = modal.querySelector('.cpc__qv-qty-input');
    if (input) input.value = this._qvQty;

    const [minus, plus] = modal.querySelectorAll('[data-cpc-qty]');
    if (minus) minus.disabled = this._qvQty <= 1;
    if (plus) plus.disabled = this._qvQty >= 3;
  }

  // ── QV add to cart ────────────────────────────────────────────────────────
  _qvAddToCart() {
    const qvBtns = this._qvModal.querySelectorAll('[data-qv-variant-id]');
    if (qvBtns.length > 0 && !this._qvSelectedVariantId) return;

    const vid = this._qvSelectedVariantId || qvBtns[0]?.dataset.qvVariantId;
    this._addToCart(vid, this._qvQty);
    this._closeQv();
  }
}

if (!customElements.get('custom-product-card')) {
  customElements.define('custom-product-card', CustomProductCard);
}
